import { $post } from '@/api'
import Iconfont from '@/components/iconfont/iconfont'
import { Column } from '@/components/layout/layout'
import { getQiniuImgUrl } from '@/config/qiniu'
import { IPagination } from '@/model/types/common'
import useBemClass from '@/utils/hooks/useBEMClass'
import { WithScrollLoad } from '@/utils/utils-widget'
import { useDynamicList, useRequest, useUpdateLayoutEffect } from 'ahooks'
import { observer } from 'mobx-react'
import React, { memo, useCallback } from 'react'
import { mallSortGoodStore as sortGoodStore } from '../store'
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import './mall-sort-goods-list.scss'
import { Spin } from 'antd'
import { errorCallBack, isExist } from '@/utils'
import { useEffect } from 'react'
import { useRef } from 'react'
import $msg from '@/utils/message'
import { FormatMallGood, MallGoodsTableList } from '../../../modules/good-table/good-table'
import { IMallGoods } from '@/model/types/mall'
import classnames from 'classnames'
import { useMemo } from 'react'

const GoodItem = (props: { img_url: string; name: string; num: number; top: boolean }) => {
    const cls = useBemClass('mall-sort-goods-list')

    const DragHandle = SortableHandle(() => (
        <div className={cls('item', 'left')}>
            <Iconfont name="icontuodong1" size={20} />
        </div>
    ))

    return (
        <div
            className={classnames(cls('item'), {
                [cls('edit-top')]: props.top,
            })}
        >
            <DragHandle />
            <div className={cls('item', 'center')}>{props.num}</div>

            <div className={cls('item', 'right')}>
                <img alt="" src={getQiniuImgUrl(props.img_url)} width="40" height="40" data-border-radius-5 />
                <div className={cls('item', 'right-txt')}>{props.name}</div>
            </div>
        </div>
    )
}

const SortGoodItem = SortableElement(GoodItem)

const SortGoodList = memo(
    SortableContainer((props: { list: FormatMallGood[]; requestHandle: any }) => {
        const cls = useBemClass('mall-sort-goods-list')

        const formatList = useMemo(() => {
            let maxTopIndex = 0
            const tmpList = props.list.map((l, index) => ({
                ...l,
                index: index,
            }))
            for (let i = 0; i < tmpList.length; i++) {
                if (tmpList[i].top) {
                    maxTopIndex++
                } else {
                    break
                }
            }

            return {
                topArr: tmpList.slice(0, maxTopIndex),
                arr: tmpList.slice(maxTopIndex),
            }
        }, [props.list])

        return (
            <WithScrollLoad data-expaned data-overflow-auto data-height-100p scrollloadhandle={props.requestHandle}>
                {formatList.topArr.map(good => (
                    <div key={good.id} className={classnames(cls('item'), [cls('edit-top')])}>
                        <div className={cls('item', 'left')}>
                            <Iconfont name="icontuodong1" size={20} />
                        </div>
                        <div className={cls('item', 'center')}>{good.index + 1}</div>
                        <div className={cls('item', 'right')}>
                            <img
                                alt=""
                                src={getQiniuImgUrl(
                                    isExist(good.mall_goods_images)
                                        ? good.mall_goods_images[0].img_url
                                        : good.goods.img_url,
                                )}
                                width="40"
                                height="40"
                                data-border-radius-5
                            />
                            <div className={cls('item', 'right-txt')}>{good.title || good.goods.name}</div>
                        </div>
                    </div>
                ))}
                {formatList.arr.map(good => (
                    <SortGoodItem
                        key={good.id}
                        index={good.index}
                        num={good.index + 1}
                        top={good.top}
                        disabled={good.top}
                        name={good.title || good.goods.name}
                        img_url={
                            isExist(good.mall_goods_images) ? good.mall_goods_images[0].img_url : good.goods.img_url
                        }
                    />
                ))}
            </WithScrollLoad>
        )
    }),
)

const MallSortGoodsList = observer(() => {
    const cls = useBemClass('mall-sort-goods-list')
    const maxIndex = useRef<number>(0)

    const requestHandle = useRequest<
        MallGoodsTableList,
        {
            mall_goods_list: IMallGoods[]
            pagination: IPagination
        }
    >(
        val => {
            return (
                // 在分组未完成加载前不加载表格数据
                sortGoodStore.activeId
                    ? $post({
                          url: '/mall/getmallgoods',
                          params: {
                              mall_group_id: sortGoodStore.activeId ? sortGoodStore.activeId : void 0,
                              mall_secondary_group_id: sortGoodStore.secondId || void 0,
                              limit: 20,
                              page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                          },
                      })
                    : new Promise(() => {})
            )
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.mall_goods_list.map(g => {
                        const group = g.groups.find(v => v.group_id === sortGoodStore.activeId)

                        return {
                            ...g,
                            top: !!(group && group.top_goods === 255),
                        }
                    }),
                    pagination: data.pagination,
                }
            },
            refreshDeps: [sortGoodStore.activeId, sortGoodStore.secondId],
        },
    )

    const sortReq = useRequest(
        () =>
            $post({
                url: '/mall/sortmallgoods',
                params: {
                    binding_ids: goodListRef.current.slice(0, maxIndex.current + 1),
                },
            }),
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '商品排序成功')
                sortGoodStore.setGoodUpdate()
            },
            onError: errorCallBack,
        },
    )

    const { list: goodList, resetList, move } = useDynamicList<FormatMallGood>([])
    const goodListRef = useRef<number[]>([])

    const moveGoodList = useCallback((oldIndex: number, newIndex: number) => {
        const newList = [...goodListRef.current]
        const temp = newList.filter((_: {}, index: number) => index !== oldIndex)
        temp.splice(newIndex, 0, newList[oldIndex])
        goodListRef.current = temp
    }, [])

    useEffect(() => {
        if (requestHandle.data) {
            resetList(requestHandle.data.list)
            goodListRef.current = requestHandle.data.list.map(g => {
                const group = g.groups.find(
                    a =>
                        a.group_id === sortGoodStore.activeId &&
                        a.secondary_group_id === (sortGoodStore.secondId || null),
                )

                return group?.group_binding_id || 0
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestHandle.data])

    useUpdateLayoutEffect(() => {
        maxIndex.current = 0
    }, [sortGoodStore.activeId, sortGoodStore.secondId])

    return (
        <Column>
            <div className={cls('title')}>商品</div>
            <Spin spinning={requestHandle.loadingMore || requestHandle.loading}>
                <Column className={cls('container')}>
                    <SortGoodList
                        list={goodList}
                        requestHandle={requestHandle}
                        axis="y"
                        distance={10}
                        helperClass="paymode-sort-helper"
                        onSortEnd={({ oldIndex, newIndex }) => {
                            if (oldIndex === newIndex) return
                            maxIndex.current = Math.max(oldIndex, newIndex, maxIndex.current)
                            move(oldIndex, newIndex)
                            moveGoodList(oldIndex, newIndex)
                            sortReq.run()
                        }}
                    />
                </Column>
            </Spin>
        </Column>
    )
})

export default MallSortGoodsList
